import React, { useMemo, useState } from "react";
import { ArrowLeft, DollarSign } from "lucide-react";
import PageShell from "../dashboard/PageShell";

const AddNewFunding: React.FC = () => {
  const id = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      // expected: topics/approval/detail/:id/funding/add → last meaningful is id at index length-3
      return parts[parts.length - 3] || "";
    } catch {
      return "";
    }
  }, []);

  const [form, setForm] = useState({
    fundingSource: "",
    proposedYear: "",
    approvedAmount: "",
    disbursedAmount: "",
    disbursementDate: "",
    notes: "",
  });

  const change = (field: string, value: string) =>
    setForm((p) => ({ ...p, [field]: value }));

  return (
    <PageShell title="Thêm kinh phí phê duyệt - đã cấp">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => {
                // Navigate back to project detail with funding tab active
                const projectId = window.location.pathname.split("/")[4]; // Extract project ID from URL
                window.location.href = `/topics/approval/detail/${projectId}`;
              }}
              aria-label="Go back"
              className="p-3 rounded-full text-[#337ab7] bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <DollarSign className="h-5 w-5 text-[#337ab7]" />
            <h1 className="text-lg font-semibold text-gray-800 truncate">
              Thêm kinh phí phê duyệt - đã cấp
            </h1>
          </div>
        </div>

        <div className="w-full  p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nguồn kinh phí <span className="text-red-500">(*)</span>
              </label>
              <select
                value={form.fundingSource}
                onChange={(e) => change("fundingSource", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">- Chọn -</option>
                <option value="ngan-sach-nha-nuoc">Ngân sách nhà nước</option>
                <option value="ngan-sach-tu-chu">Ngân sách tự chủ</option>
                <option value="kinh-phi-hop-tac">Kinh phí hợp tác</option>
                <option value="kinh-phi-khac">Kinh phí khác</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Năm đề xuất cấp <span className="text-red-500">(*)</span>
              </label>
              <select
                value={form.proposedYear}
                onChange={(e) => change("proposedYear", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">- Chọn -</option>
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kinh phí phê duyệt
              </label>
              <div className="flex">
                <input
                  type="text"
                  placeholder="Nhập số tiền"
                  value={form.approvedAmount}
                  onChange={(e) => change("approvedAmount", e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-l-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="px-3 py-2 text-sm bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-gray-600">
                  (VNĐ)
                </span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kinh phí đã cấp
              </label>
              <div className="flex">
                <input
                  type="text"
                  placeholder="Nhập số tiền"
                  value={form.disbursedAmount}
                  onChange={(e) => change("disbursedAmount", e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-l-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="px-3 py-2 text-sm bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-gray-600">
                  (VNĐ)
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ngày cấp
              </label>
              <input
                type="date"
                value={form.disbursementDate}
                onChange={(e) => change("disbursementDate", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ghi chú
              </label>
              <input
                type="text"
                placeholder="Nhập ghi chú..."
                value={form.notes}
                onChange={(e) => change("notes", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              onClick={() => window.history.back()}
            >
              Hủy
            </button>
            <button
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium shadow-sm inline-flex items-center gap-2"
              onClick={() => {
                console.log("Save funding", form);
                // Navigate back to project detail with funding tab active
                const projectId = window.location.pathname.split("/")[4]; // Extract project ID from URL
                window.location.href = `/topics/approval/detail/${projectId}`;
              }}
            >
              <DollarSign className="h-4 w-4" />
              <span>Lưu kinh phí</span>
            </button>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default AddNewFunding;
